import { useTranslation } from "react-i18next";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useActiveTabFromMatch } from "../../../../hooks/useActiveTabFromMatch";
import { TabSideNavigation } from "../../../../tab-side-navigation/TabSideNavigation";
import { TabSideNavigationItem } from "../../../../tab-side-navigation/TabSideNavigation.types";
import { useClientFileContext } from "../../../context/ClientFileContext";
import { BankruptcyChecklistSection as BankruptcyChecklistSubTab } from "../../../routes/clientFileRoutes";
import { ChecklistStatus } from "../../BankruptcyChecklist.types";
import { getChecklistTabStatuses } from "../../BankruptcyChecklist.utils";
import { useBankruptcyChecklist } from "../../hooks/useBankruptcyChecklist";

const SectionParamName = "section";

export function BankruptcyChecklistNavigation() {
  const { t } = useTranslation();
  const { clientId, basePaths } = useClientFileContext();
  const activeSubTab = useActiveTabFromMatch(
    `${basePaths.bankruptcyChecklist}/:${SectionParamName}`,
    BankruptcyChecklistSubTab,
    SectionParamName
  );
  const { data } = useBankruptcyChecklist(clientId);

  const statuses = getChecklistTabStatuses(data);

  const renderStatusIcon = (subTab: BankruptcyChecklistSubTab) =>
    statuses[subTab] === ChecklistStatus.Warning ? (
      <WarningAmberIcon
        fontSize="small"
        color="warning"
        titleAccess={t("clients.client.debtSettlement.bankruptcyChecklist.statusWarning")}
      />
    ) : (
      <CheckCircleOutlineIcon
        fontSize="small"
        color="success"
        titleAccess={t("clients.client.debtSettlement.bankruptcyChecklist.statusComplete")}
      />
    );

  const items: TabSideNavigationItem[] = [
    {
      key: BankruptcyChecklistSubTab.DangerousDebts,
      label: t("clients.client.debtSettlement.bankruptcyChecklist.tabs.dangerousDebts"),
      to: `${basePaths.bankruptcyChecklist}/${BankruptcyChecklistSubTab.DangerousDebts}`,
      endAdornment: renderStatusIcon(BankruptcyChecklistSubTab.DangerousDebts),
    },
    {
      key: BankruptcyChecklistSubTab.SpecialDebts,
      label: t("clients.client.debtSettlement.bankruptcyChecklist.tabs.specialDebts"),
      to: `${basePaths.bankruptcyChecklist}/${BankruptcyChecklistSubTab.SpecialDebts}`,
      endAdornment: renderStatusIcon(BankruptcyChecklistSubTab.SpecialDebts),
    },
    {
      key: BankruptcyChecklistSubTab.BankruptcyCapability,
      label: t("clients.client.debtSettlement.bankruptcyChecklist.tabs.bankruptcyCapability"),
      to: `${basePaths.bankruptcyChecklist}/${BankruptcyChecklistSubTab.BankruptcyCapability}`,
      endAdornment: renderStatusIcon(BankruptcyChecklistSubTab.BankruptcyCapability),
    },
    {
      key: BankruptcyChecklistSubTab.BankruptcyConsequences,
      label: t("clients.client.debtSettlement.bankruptcyChecklist.tabs.bankruptcyConsequences"),
      to: `${basePaths.bankruptcyChecklist}/${BankruptcyChecklistSubTab.BankruptcyConsequences}`,
      endAdornment: renderStatusIcon(BankruptcyChecklistSubTab.BankruptcyConsequences),
    },
  ];

  return <TabSideNavigation items={items} activeKey={activeSubTab} />;
}
